import axios from 'axios';

import config from '../config';
import {
  Configuration,
  DefaultApi,
  LegacyApi,
  PublicApi,
  V1PatientCreatePhotoCaseRequest,
  V1PatientGetMyMedicalInfo200Response,
  V1PatientGetPractitioner200Response,
  V1PatientGetPractitionersWithService200ResponseInner,
  V1PatientInitiatePaymentForPhotoCaseRequestPaymentMethodEnum,
  V1PatientInitiatePhoneVerificationRequestLocaleEnum,
  V1PatientPublicBookPhysicalAppointmentRequestPatient,
  V1PatientPublicGetAvailabilityInClinic200ResponseInner,
  V1PatientPublicGetAvailabilityInClinicWithPractitioner200ResponseInner,
  V1PatientPublicGetClinicsWithService200ResponseInner,
  V1PatientPublicGetNextAvailableTimeslotForServiceInClinic200Response,
  V1PatientPublicLegacyBookPhysicalAppointment200Response,
  V1PatientPublicLegacyBookPhysicalAppointmentRequestBookAppointmentRequestBookingOriginEnum,
  V1PatientRequestCaseImageUploadUrlRequestImageTypeEnum,
  V1PatientUpdatePhotoCaseQuestionnaireRequest,
} from '../cortex-sdk/generated-client';
import { CortexTreeNode } from './types';

export type {
  V1PatientCreatePhotoCase201Response,
  V1PatientGetCaseImageViewUrl200Response,
  V1PatientGetMe200Response,
  V1PatientGetMyMedicalInfo200Response,
  V1PatientGetMyPhotoCases200ResponseInner,
  V1PatientGetPatientDiagnosis200Response,
  V1PatientGetPhotoCaseById200Response,
  V1PatientGetPractitioner200Response,
  V1PatientGetPractitionersWithService200ResponseInner,
  V1PatientInitiatePaymentForPhotoCase200Response,
  V1PatientListCaseImages200ResponseInner,
  V1PatientPublicGetAvailabilityInClinic200ResponseInner,
  V1PatientPublicGetAvailabilityInClinic200ResponseInnerAllOfPractitionerAvailabilityInner,
  V1PatientPublicGetAvailabilityInClinicWithPractitioner200ResponseInner,
  V1PatientPublicGetNextAvailableTimeslotForServiceInClinic200Response,
  V1PatientRequestCaseImageUploadUrl200Response,
} from '../cortex-sdk/generated-client';
export {
  V1PatientInitiatePaymentForPhotoCaseRequestPaymentMethodEnum,
  V1PatientRequestCaseImageUploadUrlRequestImageTypeEnum,
} from '../cortex-sdk/generated-client';
export * from './types';

const configuration = new Configuration({
  basePath: config.NEXT_PUBLIC_CORTEX_API_URL,
});

const authenticatedConfiguration = new Configuration({
  basePath: config.NEXT_PUBLIC_CORTEX_API_URL,
  baseOptions: { withCredentials: true },
});

export const cortexPublicApi = new PublicApi(configuration);
const cortexDefaultApi = new DefaultApi(configuration);
const cortexAuthenticatedApi = new DefaultApi(authenticatedConfiguration);
const cortexLegacyApi = new LegacyApi(configuration);

export interface FetchClinicsParams {
  serviceId: string;
  signal?: AbortSignal;
}

/**
 * Fetch clinics offering a given service. Adapts the cortex response to the
 * legacy `Clinic` shape used by booking-flow-no consumers (downstream code
 * reads `address.zip`, `isActive`, `legacyShortIds`, etc.). Cortex always
 * returns active clinics, so `isActive` is hard-coded to `true` — matches the
 * legacy `filterInactive=true` behavior the booking flow used.
 */
export async function fetchClinics({
  serviceId,
  signal,
}: FetchClinicsParams): Promise<LegacyClinic[]> {
  const { data } = await cortexPublicApi.v1PatientPublicGetClinicsWithService(
    serviceId,
    { signal },
  );
  return data.map(adaptClinicWithService);
}

interface LegacyClinicAddress {
  city: string;
  street: string;
  zip: string;
  country: { code: string; name: string };
}

export interface LegacyClinic {
  id: string;
  shortId: string;
  name: string;
  isActive: boolean;
  address: LegacyClinicAddress;
  coordinates: { lat: number; lng: number };
  legacyShortIds?: string[];
}

const NORWAY_COUNTRY = { code: 'NO', name: 'Norway' };

function adaptClinicWithService(
  clinic: V1PatientPublicGetClinicsWithService200ResponseInner,
): LegacyClinic {
  return {
    id: clinic.id,
    shortId: clinic.shortId,
    name: clinic.name,
    isActive: true,
    address: {
      city: clinic.address.city,
      street: clinic.address.street,
      zip: clinic.address.postalCode,
      country: NORWAY_COUNTRY,
    },
    coordinates: clinic.coordinates,
    legacyShortIds: clinic.legacyShortIds,
  };
}

/** Fetch service tree — not in the generated SDK due to recursive schema issues */
export async function fetchServiceTree(
  organizationId: string,
  partnerId?: string,
): Promise<CortexTreeNode[]> {
  const params: Record<string, string> = { organizationId };
  if (partnerId) params.partnerId = partnerId;
  const response = await axios.get<CortexTreeNode[]>(
    `${config.NEXT_PUBLIC_CORTEX_API_URL}/v1/patient/public/services/tree`,
    { params },
  );
  return response.data;
}

/** Fetch the Storebrand-filtered service tree — not in the generated SDK due to recursive schema issues */
export async function fetchStorebrandServiceTree(
  organizationId: string,
): Promise<CortexTreeNode[]> {
  const response = await axios.get<CortexTreeNode[]>(
    `${config.NEXT_PUBLIC_CORTEX_API_URL}/v1/patient/public/services/legacy/tree/storebrand`,
    { params: { organizationId } },
  );
  return response.data;
}

/**
 * Minimal practitioner shape read by booking-flow-no consumers
 * (SelectPractitionerStep, PractitionerTimePickerList, BookingSummary,
 * PractitionerInfo). Field names mirror the legacy `@packages/types`
 * Practitioner so call sites can swap to cortex without renaming props.
 */
export interface LegacyPractitioner {
  id: string;
  name: string;
  photoUrl: string;
  description: { nb: string; en: string };
  clinics: { id: string; name: string; city: string }[];
}

export interface FetchPractitionersParams {
  serviceId: string;
  clinicId?: string;
  signal?: AbortSignal;
}

/**
 * Fetch practitioners offering a given service, optionally narrowed to one
 * clinic. Cortex splits this across two endpoints; which one we hit depends on
 * whether a clinic is supplied. Results are sorted by name to match the legacy
 * `GET /v3/practitioners` behavior downstream code relies on.
 */
export async function fetchPractitioners({
  serviceId,
  clinicId,
  signal,
}: FetchPractitionersParams): Promise<LegacyPractitioner[]> {
  const { data } = clinicId
    ? await cortexPublicApi.v1PatientGetPractitionersInClinicWithService(
        serviceId,
        clinicId,
        { signal },
      )
    : await cortexPublicApi.v1PatientGetPractitionersWithService(serviceId, {
        signal,
      });
  return data
    .map(adaptPractitionerListItem)
    .sort((a, b) => a.name.localeCompare(b.name));
}

export interface FetchPractitionerParams {
  practitionerId: string;
  signal?: AbortSignal;
}

export async function fetchPractitioner({
  practitionerId,
  signal,
}: FetchPractitionerParams): Promise<LegacyPractitioner> {
  const { data } = await cortexPublicApi.v1PatientGetPractitioner(
    practitionerId,
    { signal },
  );
  return adaptPractitionerDetail(data);
}

export interface GetPractitionerByIdParams {
  practitionerId: string;
  signal?: AbortSignal;
}

/** Get a practitioner by ID, returning the full, unadapted response (e.g. includes `longName`). */
export async function getPractitionerById({
  practitionerId,
  signal,
}: GetPractitionerByIdParams): Promise<V1PatientGetPractitioner200Response> {
  const { data } = await cortexPublicApi.v1PatientGetPractitioner(
    practitionerId,
    { signal },
  );
  return data;
}

function adaptPractitionerListItem(
  p: V1PatientGetPractitionersWithService200ResponseInner,
): LegacyPractitioner {
  return {
    id: p.id,
    name: p.shortName,
    photoUrl: p.pictureUrl ?? '',
    description: {
      nb: p.description?.nb ?? '',
      en: p.description?.en ?? '',
    },
    clinics: p.clinics,
  };
}

function adaptPractitionerDetail(
  p: V1PatientGetPractitioner200Response,
): LegacyPractitioner {
  return {
    id: p.id,
    name: p.shortName,
    photoUrl: p.pictureUrl ?? '',
    description: {
      nb: p.description?.nb ?? '',
      en: p.description?.en ?? '',
    },
    clinics: p.clinics,
  };
}

/**
 * Minimal timeslot shape used by booking-flow-no UI. Mirrors the fields the
 * frontend reads from the legacy Core-API `TimeSlot` so call sites don't need
 * to change — `clinicId`/`practitionerId`/`availablePractitionersByIds`/
 * `availableSpotsByPractitionerIds` are synthesized from the cortex response
 * + request params.
 */
export interface CortexTimeSlot {
  serviceId: string;
  startTime: string;
  isAvailable: boolean;
  clinicId: string;
  practitionerId: string;
  duration: number;
  availablePractitionersByIds: string[];
  availableSpotsByPractitionerIds: Record<string, number>;
}

export interface FetchTimeSlotsParams {
  serviceId: string;
  clinicId: string;
  fromDate?: string;
  toDate?: string;
  practitionerId?: string;
  signal?: AbortSignal;
}

export async function fetchTimeSlots({
  serviceId,
  clinicId,
  fromDate,
  toDate,
  practitionerId,
  signal,
}: FetchTimeSlotsParams): Promise<CortexTimeSlot[]> {
  if (practitionerId) {
    const { data } =
      await cortexPublicApi.v1PatientPublicGetAvailabilityInClinicWithPractitioner(
        serviceId,
        clinicId,
        practitionerId,
        fromDate,
        toDate,
        { signal },
      );
    return data.map((slot) =>
      adaptWithPractitionerSlot(slot, clinicId, practitionerId),
    );
  }

  const { data } = await cortexPublicApi.v1PatientPublicGetAvailabilityInClinic(
    serviceId,
    clinicId,
    fromDate,
    toDate,
    { signal },
  );
  return data.map((slot) => adaptInClinicSlot(slot, clinicId));
}

function adaptInClinicSlot(
  slot: V1PatientPublicGetAvailabilityInClinic200ResponseInner,
  clinicId: string,
): CortexTimeSlot {
  const availablePractitionersByIds: string[] = [];
  const availableSpotsByPractitionerIds: Record<string, number> = {};
  for (const p of slot.practitionerAvailability ?? []) {
    availableSpotsByPractitionerIds[p.practitionerId] = p.numAvailableSpots;
    if (p.isAvailable && p.numAvailableSpots > 0) {
      availablePractitionersByIds.push(p.practitionerId);
    }
  }
  return {
    serviceId: slot.serviceId,
    startTime: slot.startTime,
    isAvailable: slot.isAvailable,
    clinicId,
    practitionerId: '',
    duration: 0,
    availablePractitionersByIds,
    availableSpotsByPractitionerIds,
  };
}

function adaptWithPractitionerSlot(
  slot: V1PatientPublicGetAvailabilityInClinicWithPractitioner200ResponseInner,
  clinicId: string,
  practitionerId: string,
): CortexTimeSlot {
  return {
    serviceId: slot.serviceId,
    startTime: slot.startTime,
    isAvailable: slot.isAvailable,
    clinicId,
    practitionerId,
    duration: 0,
    availablePractitionersByIds:
      slot.isAvailable && slot.numAvailableSpots > 0 ? [practitionerId] : [],
    availableSpotsByPractitionerIds: {
      [practitionerId]: slot.numAvailableSpots,
    },
  };
}

export interface FetchTimeSlotsAcrossClinicsParams {
  serviceId: string;
  practitionerId: string;
  fromDate?: string;
  toDate?: string;
  signal?: AbortSignal;
}

export async function fetchTimeSlotsAcrossClinics({
  serviceId,
  practitionerId,
  fromDate,
  toDate,
  signal,
}: FetchTimeSlotsAcrossClinicsParams): Promise<CortexTimeSlot[]> {
  const { data } =
    await cortexPublicApi.v1PatientPublicGetAvailabilityAcrossClinicsWithPractitioner(
      serviceId,
      practitionerId,
      fromDate,
      toDate,
      { signal },
    );

  return data.flatMap((slot) => {
    return slot.practitionerAvailability.map((entry): CortexTimeSlot => {
      const isAvailable = entry.isAvailable && entry.numAvailableSpots > 0;
      return {
        serviceId: slot.serviceId,
        startTime: slot.startTime,
        isAvailable,
        clinicId: entry.clinicId,
        practitionerId,
        duration: 0,
        availablePractitionersByIds: isAvailable ? [practitionerId] : [],
        availableSpotsByPractitionerIds: {
          [practitionerId]: entry.numAvailableSpots,
        },
      };
    });
  });
}

export interface FetchNextAvailableParams {
  serviceId: string;
  clinicId: string;
  practitionerId?: string;
  fromDate?: string;
  signal?: AbortSignal;
}

export async function fetchNextAvailable({
  serviceId,
  clinicId,
  practitionerId,
  fromDate,
  signal,
}: FetchNextAvailableParams): Promise<{ startTime: string } | null> {
  if (practitionerId) {
    const { data } =
      await cortexDefaultApi.v1PatientPublicGetNextAvailableTimeslotForServiceInClinicWithPractitioner(
        serviceId,
        clinicId,
        practitionerId,
        fromDate,
        { signal },
      );
    return unwrapNextAvailable(data);
  }

  const { data } =
    await cortexDefaultApi.v1PatientPublicGetNextAvailableTimeslotForServiceInClinic(
      serviceId,
      clinicId,
      fromDate,
      { signal },
    );
  return unwrapNextAvailable(data);
}

export interface FetchNextAvailableAcrossClinicsParams {
  serviceId: string;
  practitionerId: string;
  fromDate?: string;
  clinicIds?: string[];
  signal?: AbortSignal;
}

export async function fetchNextAvailableAcrossClinics({
  serviceId,
  practitionerId,
  fromDate,
  clinicIds,
  signal,
}: FetchNextAvailableAcrossClinicsParams): Promise<{
  startTime: string;
} | null> {
  const { data } =
    await cortexDefaultApi.v1PatientPublicGetNextAvailableTimeslotForServiceWithPractitionerAcrossClinics(
      serviceId,
      practitionerId,
      clinicIds ?? [],
      fromDate,
      { signal },
    );
  return unwrapNextAvailable(data);
}

function unwrapNextAvailable(
  data:
    | V1PatientPublicGetNextAvailableTimeslotForServiceInClinic200Response
    | null
    | undefined,
): { startTime: string } | null {
  if (!data || !data.startTime) return null;
  return { startTime: data.startTime };
}

const BookingOriginWeb =
  V1PatientPublicLegacyBookPhysicalAppointmentRequestBookAppointmentRequestBookingOriginEnum.Web;

export interface BookPhysicalAppointmentParams {
  serviceId: string;
  clinicId: string;
  startTime: string;
  description: string;
  patient: V1PatientPublicBookPhysicalAppointmentRequestPatient;
  practitionerId?: string;
  partnerCampaignId?: string;
  signal?: AbortSignal;
}

export async function bookPhysicalAppointment({
  serviceId,
  clinicId,
  startTime,
  description,
  patient,
  practitionerId,
  partnerCampaignId,
  signal,
}: BookPhysicalAppointmentParams): Promise<V1PatientPublicLegacyBookPhysicalAppointment200Response> {
  if (practitionerId) {
    const { data } =
      await cortexLegacyApi.v1PatientPublicLegacyBookPhysicalAppointmentWithPractitioner(
        {
          bookAppointmentRequest: {
            serviceId,
            clinicId,
            practitionerId,
            startTime,
            description,
            bookingOrigin: BookingOriginWeb,
            ...(partnerCampaignId ? { partnerCampaignId } : {}),
          },
          patient,
        },
        { signal },
      );
    return data;
  }

  const { data } =
    await cortexLegacyApi.v1PatientPublicLegacyBookPhysicalAppointment(
      {
        bookAppointmentRequest: {
          serviceId,
          clinicId,
          startTime,
          description,
          bookingOrigin: BookingOriginWeb,
          ...(partnerCampaignId ? { partnerCampaignId } : {}),
        },
        patient,
      },
      { signal },
    );
  return data;
}

export interface InitiatePhoneVerificationParams {
  phoneNumber: string;
  locale?: string;
  token: string;
}

export interface PhoneVerificationResponse {
  status: string;
  valid: boolean;
}

export async function initiatePhoneVerification({
  phoneNumber,
  locale,
  token,
}: InitiatePhoneVerificationParams): Promise<PhoneVerificationResponse> {
  const { data } = await cortexPublicApi.v1PatientInitiatePhoneVerification({
    phone: phoneNumber,
    ...(locale && isSupportedLocale(locale) ? { locale } : {}),
    token,
  });
  return { status: data.status, valid: data.status === 'pending' };
}

export interface VerifyPhoneNumberParams {
  phoneNumber: string;
  code: string;
}

export async function verifyPhoneNumber({
  phoneNumber,
  code,
}: VerifyPhoneNumberParams): Promise<PhoneVerificationResponse> {
  const { data } = await cortexPublicApi.v1PatientVerifyPhoneNumber({
    phone: phoneNumber,
    code,
  });
  return { status: data.status, valid: data.verified };
}

function isSupportedLocale(
  locale: string,
): locale is V1PatientInitiatePhoneVerificationRequestLocaleEnum {
  return (
    locale === V1PatientInitiatePhoneVerificationRequestLocaleEnum.Nb ||
    locale === V1PatientInitiatePhoneVerificationRequestLocaleEnum.En
  );
}

// ---------------------------------------------------------------------------
// Photo cases (authenticated)
//
// The photo-case and case-image endpoints are browser-only: the patient's
// httpOnly cortex session cookie is sent automatically via `withCredentials`
// on the shared axios Configuration, and mutating calls additionally carry
// the caller's CSRF token in the `x-csrf-token` header. The CSRF token is
// threaded per call rather than configured on a shared client instance so
// the SDK stays stateless and safe to use from per-request server contexts
// (no token bleed across requests/users).
// ---------------------------------------------------------------------------

/** Builds the per-request axios options, adding the CSRF header if a token is provided. */
function authRequestConfig({
  signal,
  csrfToken,
}: {
  signal?: AbortSignal;
  csrfToken?: string;
}) {
  if (csrfToken) {
    return {
      headers: {
        'x-csrf-token': csrfToken,
      },
      signal,
    };
  }
  return {
    signal,
  };
}

export type PhotoCaseImageType =
  V1PatientRequestCaseImageUploadUrlRequestImageTypeEnum;
export type PhotoCasePaymentMethod =
  V1PatientInitiatePaymentForPhotoCaseRequestPaymentMethodEnum;

export interface CreatePhotoCaseParams {
  serviceId: string;
  category?: string | null;
  childId?: string | null;
  questionnaire?: V1PatientCreatePhotoCaseRequest['questionnaire'];
  csrfToken: string;
  signal?: AbortSignal;
}

/** Create a new draft photo case for the authenticated patient. */
export async function createPhotoCase({
  serviceId,
  category,
  childId,
  questionnaire,
  csrfToken,
  signal,
}: CreatePhotoCaseParams) {
  const { data } = await cortexAuthenticatedApi.v1PatientCreatePhotoCase(
    { serviceId, category, childId, questionnaire },
    authRequestConfig({ signal, csrfToken }),
  );
  return data;
}

export interface UpdatePhotoCaseQuestionnaireParams {
  photoCaseId: string;
  category?: V1PatientUpdatePhotoCaseQuestionnaireRequest['category'];
  childId?: V1PatientUpdatePhotoCaseQuestionnaireRequest['childId'];
  questionnaire?: V1PatientUpdatePhotoCaseQuestionnaireRequest['questionnaire'];
  csrfToken: string;
  signal?: AbortSignal;
}

/** Update the editable fields (category, child, questionnaire) of a draft case. */
export async function updatePhotoCaseQuestionnaire({
  photoCaseId,
  category,
  childId,
  questionnaire,
  csrfToken,
  signal,
}: UpdatePhotoCaseQuestionnaireParams) {
  const { data } =
    await cortexAuthenticatedApi.v1PatientUpdatePhotoCaseQuestionnaire(
      photoCaseId,
      { category, childId, questionnaire },
      authRequestConfig({ signal, csrfToken }),
    );
  return data;
}

export interface RequestUploadUrlParams {
  photoCaseId: string;
  imageType: PhotoCaseImageType;
  mimeType: string;
  declaredSizeBytes?: number | null;
  csrfToken: string;
  signal?: AbortSignal;
}

/**
 * Request a signed PUT URL for a case image. Returns the `caseImageId` and the
 * `uploadUrl` the client PUTs the file bytes to directly; the upload is then
 * finalized with `confirmUpload`.
 */
export async function requestUploadUrl({
  photoCaseId,
  imageType,
  mimeType,
  declaredSizeBytes,
  csrfToken,
  signal,
}: RequestUploadUrlParams) {
  const { data } =
    await cortexAuthenticatedApi.v1PatientRequestCaseImageUploadUrl(
      { photoCaseId, imageType, mimeType, declaredSizeBytes },
      authRequestConfig({ signal, csrfToken }),
    );
  return data;
}

export interface ConfirmUploadParams {
  caseImageId: string;
  csrfToken: string;
  signal?: AbortSignal;
}

/** Confirm a completed upload, marking the case image present and visible. */
export async function confirmUpload({
  caseImageId,
  csrfToken,
  signal,
}: ConfirmUploadParams) {
  const { data } = await cortexAuthenticatedApi.v1PatientConfirmCaseImageUpload(
    caseImageId,
    authRequestConfig({ signal, csrfToken }),
  );
  return data;
}

export interface SubmitPhotoCaseParams {
  photoCaseId: string;
  csrfToken: string;
  signal?: AbortSignal;
}

/** Submit a draft case for review without payment (free-pass / no-cost cases). */
export async function submitPhotoCase({
  photoCaseId,
  csrfToken,
  signal,
}: SubmitPhotoCaseParams) {
  const { data } = await cortexAuthenticatedApi.v1PatientSubmitPhotoCase(
    photoCaseId,
    authRequestConfig({ signal, csrfToken }),
  );
  return data;
}

export interface InitiatePaymentForPhotoCaseParams {
  photoCaseId: string;
  paymentMethod: PhotoCasePaymentMethod;
  // undefined → use the patient's default card; null → no card (e.g. Vipps).
  paymentCardId?: string | null;
  returnPath?: string;
  csrfToken: string;
  signal?: AbortSignal;
}

/**
 * Submit a draft case and mint a Dintero payment session in one call. Returns
 * the `authorizationUrl` the client redirects to; the case transitions
 * `draft → submitted` with the order linked.
 */
export async function initiatePaymentForPhotoCase({
  photoCaseId,
  paymentMethod,
  paymentCardId,
  returnPath,
  csrfToken,
  signal,
}: InitiatePaymentForPhotoCaseParams) {
  const { data } =
    await cortexAuthenticatedApi.v1PatientInitiatePaymentForPhotoCase(
      photoCaseId,
      { paymentMethod, paymentCardId, returnPath },
      authRequestConfig({ signal, csrfToken }),
    );
  return data;
}

export interface GetMyPhotoCasesParams {
  signal?: AbortSignal;
}

/** List the authenticated patient's photo cases, newest first. */
export async function getMyPhotoCases({ signal }: GetMyPhotoCasesParams) {
  const { data } = await cortexAuthenticatedApi.v1PatientGetMyPhotoCases(
    authRequestConfig({ signal }),
  );
  return data;
}

export interface GetPhotoCaseByIdParams {
  photoCaseId: string;
  signal?: AbortSignal;
}

/** Get one of the authenticated patient's photo cases by ID. */
export async function getPhotoCaseById({
  photoCaseId,
  signal,
}: GetPhotoCaseByIdParams) {
  const { data } = await cortexAuthenticatedApi.v1PatientGetPhotoCaseById(
    photoCaseId,
    authRequestConfig({ signal }),
  );
  return data;
}

export interface GetMeParams {
  cookie?: string; // forwarded Cookie header, needed only for server-side calls
  signal?: AbortSignal;
}

export async function getMe({ cookie, signal }: GetMeParams = {}) {
  const { data } = await cortexAuthenticatedApi.v1PatientGetMe({
    signal,
    withCredentials: true,
    ...(cookie ? { headers: { Cookie: cookie } } : {}),
  });
  return data;
}

export interface GetDiagnosisByPhotoCaseIdParams {
  photoCaseId: string;
  signal?: AbortSignal;
}
/** Get an authenticated patient's diagnosis by photocase ID. */
export async function getPatientDiagnosisByPhotoCaseId({
  photoCaseId,
  signal,
}: GetDiagnosisByPhotoCaseIdParams) {
  const { data } = await cortexAuthenticatedApi.v1PatientGetPatientDiagnosis(
    photoCaseId,
    authRequestConfig({ signal }),
  );
  return data;
}

export function isAuthError(error: unknown): boolean {
  return axios.isAxiosError(error) && error.response?.status === 401;
}

/** Get an authenticated patient's medical info */
export async function getPatientMedicalInfo({
  signal,
}: {
  signal?: AbortSignal;
}): Promise<V1PatientGetMyMedicalInfo200Response | null> {
  const { data } = await cortexAuthenticatedApi.v1PatientGetMyMedicalInfo(
    authRequestConfig({ signal }),
  );
  return data;
}

export interface UpsertMyMedicalInfoParams {
  hasAllergies: boolean;
  allergies: string | null;
  hasMedication: boolean;
  medication: string | null;
  csrfToken: string;
}
/** Upsert an authenticated patient's medical info */
export async function upsertMyMedicalInfo({
  hasAllergies,
  allergies,
  hasMedication,
  medication,
  csrfToken,
}: UpsertMyMedicalInfoParams) {
  const { data } = await cortexAuthenticatedApi.v1PatientUpsertMyMedicalInfo(
    { hasAllergies, allergies, hasMedication, medication },
    authRequestConfig({ csrfToken }),
  );
  return data;
}

export interface GetPatientCaseImagesByPhotoCaseIdParams {
  photoCaseId: string;
  signal?: AbortSignal;
}
/** Get an authenticated patient's case images by photocase ID. */
export async function getPatientCaseImagesByPhotoCaseId({
  photoCaseId,
  signal,
}: GetPatientCaseImagesByPhotoCaseIdParams) {
  const { data } = await cortexAuthenticatedApi.v1PatientListCaseImages(
    photoCaseId,
    authRequestConfig({ signal }),
  );
  return data;
}

export interface GetPatientCaseImageViewUrlByCaseImageIdParams {
  caseImageId: string;
  signal?: AbortSignal;
}
/** Get an authenticated patient's case image view url by case image ID. */
export async function getCaseImageViewUrlByCaseImageId({
  caseImageId,
  signal,
}: GetPatientCaseImageViewUrlByCaseImageIdParams) {
  const { data } = await cortexAuthenticatedApi.v1PatientGetCaseImageViewUrl(
    caseImageId,
    authRequestConfig({ signal }),
  );
  return data;
}
